// ============================================================================
// BANK RECONCILIATION SERVICE (ADR-0055 §7-8) — the Reconciliation Start of a
// bank account (start date, bank balance on it, items outstanding on it) and
// the Bank Reconciliation Statement as at a date, with its Excel export.
// ============================================================================

import { del, get, put } from '../client/axiosClient';
import { bankReconciliationEndpoints } from '../endpoints/banking.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import { downloadBlob } from './reportService';
import type {
  ApiResponse, ID, ReconciliationStartDto, SaveReconciliationStartRequest, BankReconciliationStatementDto,
} from '../types';

class BankReconciliationService {
  /** The bank's start, or null when none is set. */
  async getStart(bankLedgerId: ID): Promise<ReconciliationStartDto | null> {
    const response = await get<ApiResponse<ReconciliationStartDto | null>>(
      buildEndpointWithQuery(bankReconciliationEndpoints.start(), { bankLedgerId }));
    return response.data.data ?? null;
  }

  /** Creates or replaces the start; the opening items are the full list. */
  async saveStart(bankLedgerId: ID, data: SaveReconciliationStartRequest): Promise<ReconciliationStartDto> {
    const response = await put<ApiResponse<ReconciliationStartDto>>(
      buildEndpointWithQuery(bankReconciliationEndpoints.start(), { bankLedgerId }), data);
    return response.data.data;
  }

  async deleteStart(bankLedgerId: ID): Promise<void> {
    await del(buildEndpointWithQuery(bankReconciliationEndpoints.start(), { bankLedgerId }));
  }

  async getStatement(bankLedgerId: ID, asAt: string): Promise<BankReconciliationStatementDto> {
    const response = await get<ApiResponse<BankReconciliationStatementDto>>(
      buildEndpointWithQuery(bankReconciliationEndpoints.statement(), { bankLedgerId, asAt }));
    return response.data.data;
  }

  async exportStatement(bankLedgerId: ID, asAt: string): Promise<void> {
    const response = await get<Blob>(
      buildEndpointWithQuery(bankReconciliationEndpoints.statementExport(), { bankLedgerId, asAt }), { responseType: 'blob' });
    downloadBlob(response, `BankReconciliation_${asAt}.xlsx`);
  }
}

export const bankReconciliationService = new BankReconciliationService();
